/**
 * Provider Recruitment Component
 * Displays potential providers for CBO and allows sponsors to recruit them
 * Part of Hybrid Provider Scoring System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  UserPlus,
  AlertCircle,
  CheckCircle2,
  Star,
  MapPin,
  Phone,
  Mail,
  Building2,
  TrendingUp,
  Heart
} from 'lucide-react';

interface PotentialProvider {
  provider_id: string;
  name: string;
  specialties: string[];
  facility_type: string;
  quality_score: number;
  accepts_medicaid: boolean;
  county: string;
  phone: string;
  email: string;
  status: string;
}

interface ProviderRecruitmentProps {
  cboId: number;
  cboName: string;
  sponsorId: number;
  activeProviderCount: number;
  potentialProviderCount: number;
  onRecruitmentComplete?: () => void;
}

const ProviderRecruitment: React.FC<ProviderRecruitmentProps> = ({
  cboId,
  cboName,
  sponsorId,
  activeProviderCount,
  potentialProviderCount,
  onRecruitmentComplete
}) => {
  const { t } = useAppTranslation();
  const [providers, setProviders] = useState<PotentialProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [selectedProvider, setSelectedProvider] = useState<PotentialProvider | null>(null);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [recruiting, setRecruiting] = useState(false);

  const fetchPotentialProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = getWeaveinApiUrl(`/api/cbos/${cboId}/potential-providers`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch potential providers: ${response.status}`);
      }

      const data = await response.json();
      setProviders(data.providers || []);
    } catch (err) {
      console.error('Error fetching potential providers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch potential providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchPotentialProviders();
    }
  }, [showModal, cboId]);

  const handleRecruitProvider = async () => {
    if (!selectedProvider) return;

    try {
      setRecruiting(true);
      const url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/recruit-provider`);

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          provider_directory_id: selectedProvider.provider_id,
          cbo_id: cboId,
          invitation_message: invitationMessage
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send recruitment invitation');
      }

      alert(`Recruitment invitation sent to ${selectedProvider.name}! They will receive an email to join the Trust network.`);

      setSelectedProvider(null);
      setInvitationMessage('');
      fetchPotentialProviders(); // Refresh list

      if (onRecruitmentComplete) {
        onRecruitmentComplete();
      }

    } catch (err) {
      console.error('Error recruiting provider:', err);
      alert(err instanceof Error ? err.message : 'Failed to send recruitment invitation');
    } finally {
      setRecruiting(false);
    }
  };

  const getQualityColor = (score: number) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 4.0) return 'text-blue-600';
    if (score >= 3.5) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        className="relative"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        View Recruitment Opportunities
        {potentialProviderCount > 0 && (
          <Badge className="ml-2 bg-orange-500 text-white">{potentialProviderCount}</Badge>
        )}
      </Button>

      {/* Recruitment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Provider Recruitment for {cboName}</DialogTitle>
            <DialogDescription>
              {t('marketplace.recruit_providers_from_the_provider', 'Recruit providers from the Provider Directory to join the Trust network and improve match scores')}
            </DialogDescription>
          </DialogHeader>

          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg mb-4">
            <div>
              <div className="text-2xl font-bold text-blue-600">{activeProviderCount}</div>
              <div className="text-sm text-gray-600">{t('marketplace.active_trust_network_providers', 'Active Trust Network Providers')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('marketplace.contributing_10_to_match_score', 'Contributing 10% to match score')}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{potentialProviderCount}</div>
              <div className="text-sm text-gray-600">{t('marketplace.potential_providers_available', 'Potential Providers Available')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('marketplace.currently_contributing_5_to_match', 'Currently contributing 5% to match score')}</div>
            </div>
          </div>

          {/* Impact Estimate */}
          {potentialProviderCount > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-medium text-green-800">{t('marketplace.recruitment_impact', 'Recruitment Impact')}</div>
                  <div className="text-sm text-green-700">
                    Recruiting these {potentialProviderCount} providers could increase this CBO's match score by up to {Math.round(potentialProviderCount * 0.5)}%
                    (potential providers would become active, contributing 10% instead of 5%)
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Providers List */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 p-4 bg-red-50 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <p>Error: {error}</p>
            </div>
          )}

          {!loading && !error && providers.length === 0 && (
            <div className="text-center py-12">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('marketplace.all_providers_recruited', 'All Providers Recruited!')}</h3>
              <p className="text-gray-600">
                {t('marketplace.this_cbos_entire_provider_network', 'This CBO\'s entire provider network is already active in the Trust network.')}
              </p>
            </div>
          )}

          {!loading && !error && providers.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Potential Providers ({providers.length})</h3>

              {providers.filter(provider => provider && provider.provider_id).map((provider) => (
                <Card key={provider.provider_id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-gray-500" />
                        <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                        {provider.accepts_medicaid && (
                          <Badge variant="outline" className="text-xs">{t('marketplace.medicaid', 'Medicaid')}</Badge>
                        )}
                      </div>

                      {/* Specialties */}
                      {provider.specialties && provider.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {provider.specialties.map((specialty, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              <Heart className="w-3 h-3 mr-1" />
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Details */}
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        {provider.facility_type && (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            <span>{provider.facility_type}</span>
                          </div>
                        )}
                        {provider.county && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span>{provider.county}</span>
                          </div>
                        )}
                        {provider.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span className="truncate">{provider.phone}</span>
                          </div>
                        )}
                        {provider.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{provider.email}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quality & Action */}
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {provider.quality_score && (
                        <div className={`flex items-center gap-1 ${getQualityColor(provider.quality_score)}`}>
                          <Star className="w-4 h-4 fill-current" />
                          <span className="font-medium">{provider.quality_score.toFixed(1)}</span>
                        </div>
                      )}

                      <Button
                        size="sm"
                        onClick={() => setSelectedProvider(provider)}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        {t('marketplace.recruit', 'Recruit')}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recruitment Invitation Modal */}
      {selectedProvider && (
        <Dialog open={!!selectedProvider} onOpenChange={() => setSelectedProvider(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recruit {selectedProvider.name}</DialogTitle>
              <DialogDescription>
                Invite this provider to join the Trust network as a partner for {cboName}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Provider Summary */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900 mb-1">{selectedProvider.name}</div>
                <div className="text-sm text-gray-600">
                  {selectedProvider.specialties.join(', ')}
                </div>
                {selectedProvider.quality_score && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="text-sm font-medium">{selectedProvider.quality_score.toFixed(1)} / 5.0</span>
                  </div>
                )}
              </div>

              {/* Invitation Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('marketplace.invitation_message', 'Invitation Message')}
                </label>
                <textarea
                  value={invitationMessage}
                  onChange={(e) => setInvitationMessage(e.target.value)}
                  placeholder={`Dear ${selectedProvider.name},\n\nWe'd like to invite you to join the Trust network as a provider partner for ${cboName}. By joining, you'll be able to collaborate on programs and receive referrals from our sponsor network.\n\nBest regards`}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedProvider(null)}
                  disabled={recruiting}
                >
                  {t('marketplace.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={handleRecruitProvider}
                  disabled={recruiting}
                >
                  {recruiting ? (
                    <>
                      <LoadingSpinner className="w-4 h-4 mr-2" />
                      {t('marketplace.sending_invitation', 'Sending Invitation...')}
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t('marketplace.send_recruitment_invitation', 'Send Recruitment Invitation')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ProviderRecruitment;
